import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import AdminDashboard   from './AdminDashboard';
import AdminMagazines   from './AdminMagazines';
import AdminArticles    from './AdminArticles';
import AdminUploads     from './AdminUploads';
import AdminSubscribers from './AdminSubscribers';
import AdminTrends      from './AdminTrends';
import '../../css/admin/admin.css';

/* ── Icon helper ── */
const Icon = ({ d, size = 18 }) => (
  <svg width={size} height={size} fill="none" viewBox="0 0 24 24"
    stroke="currentColor" strokeWidth="1.8">
    <path strokeLinecap="round" strokeLinejoin="round" d={d}/>
  </svg>
);

const I = {
  dashboard:   'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  magazine:    'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  articles:    'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  uploads:     'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
  subscribers: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
  trends:      'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
  logout:      'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1',
  menu:        'M4 6h16M4 12h16M4 18h16',
  bell:        'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
  home:        'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
};

const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [{ label:'Dashboard',    path:'/admin',             icon: I.dashboard   }],
  },
  {
    label: 'Content',
    items: [
      { label:'Magazines',          path:'/admin/magazines',   icon: I.magazine    },
      { label:'Articles',           path:'/admin/articles',    icon: I.articles    },
      { label:'Trends',             path:'/admin/trends',      icon: I.trends      },
    ],
  },
  {
    label: 'Media',
    items: [{ label:'Uploads & Media', path:'/admin/uploads',  icon: I.uploads     }],
  },
  {
    label: 'Audience',
    items: [{ label:'Subscribers',  path:'/admin/subscribers', icon: I.subscribers }],
  },
];

const LogoSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 44" width="180" height="32">
    <rect fill="#c9a84c" x="0" y="0" width="3" height="44"/>
    <text fontFamily="'Playfair Display',Georgia,serif" fontWeight="700" fontSize="16"
      fill="#ffffff" x="10" y="28">
      The Elite <tspan fill="#c9a84c">Global</tspan> Review
    </text>
  </svg>
);

function Clock() {
  const [t, setT] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setT(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="topbar-time">
      {t.toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}
    </span>
  );
}

export default function AdminLayout({ admin, onLogout }) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobOpen,   setMobOpen]   = useState(false);
  const location = useLocation();
  const isDemoMode = localStorage.getItem('adminMode') === 'demo';
  const initials = (admin?.name || 'AD').split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase();

  const isMobile = () => window.innerWidth < 768;
  const closeDrawer = () => setMobOpen(false);

  const currentLabel = NAV_GROUPS
    .flatMap(g => g.items)
    .find(i => i.path === location.pathname)?.label || 'Dashboard';

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="admin-root">

      {/* ────────── SIDEBAR ────────── */}
      <aside className={[
        'admin-sidebar',
        collapsed && !isMobile() ? 'collapsed' : '',
        mobOpen ? 'mob-open' : '',
      ].filter(Boolean).join(' ')}>

        <div className="sidebar-logo">
          <LogoSVG/>
          {isDemoMode && (
            <div style={{ marginTop:8, padding:'4px 10px', background:'rgba(201,168,76,.12)',
              border:'1px solid rgba(201,168,76,.25)', borderRadius:3,
              fontSize:'.62rem', fontWeight:700, letterSpacing:'.1em',
              textTransform:'uppercase', color:'var(--gold)', display:'inline-block' }}>
              Demo Mode
            </div>
          )}
        </div>

        <nav className="sidebar-nav">
          {NAV_GROUPS.map(group => (
            <div key={group.label} className="nav-group">
              <div className="nav-group-label">{group.label}</div>
              {group.items.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`nav-item${isActive(item.path) ? ' active' : ''}`}
                  onClick={closeDrawer}
                >
                  <Icon d={item.icon}/>{item.label}
                </Link>
              ))}
            </div>
          ))}

          {/* Back to site */}
          <div className="nav-group" style={{ marginTop:8 }}>
            <div className="nav-group-label">Site</div>
            <Link to="/" className="nav-item" onClick={closeDrawer} target="_blank">
              <Icon d={I.home}/> View Site ↗
            </Link>
          </div>
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="sidebar-avatar">{initials}</div>
            <div className="sidebar-user-info">
              <div className="sidebar-user-name">{admin?.name || 'Admin'}</div>
              <div className="sidebar-user-role">{admin?.role || 'super'}</div>
            </div>
          </div>
          <button className="btn-logout" onClick={onLogout}>
            <Icon d={I.logout} size={15}/> Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobOpen && (
        <div
          onClick={closeDrawer}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.65)', zIndex:199 }}
        />
      )}

      {/* ────────── TOPBAR ────────── */}
      <header className={`admin-topbar${collapsed && !isMobile() ? ' full' : ''}`}>
        <div className="topbar-left">
          <button
            className="btn-sidebar-toggle"
            onClick={() => isMobile() ? setMobOpen(o => !o) : setCollapsed(c => !c)}
            aria-label="Toggle sidebar"
          >
            <Icon d={I.menu}/>
          </button>
          <div className="topbar-breadcrumb">
            Admin <span>/ {currentLabel}</span>
          </div>
        </div>
        <div className="topbar-right">
          <Clock/>
          <div className="topbar-notif" title="Notifications">
            <Icon d={I.bell} size={16}/>
            <span className="notif-dot"/>
          </div>
          <div className="topbar-avatar" title={admin?.email}>{initials}</div>
        </div>
      </header>

      {/* ────────── MAIN CONTENT ────────── */}
      <main className={`admin-main${collapsed && !isMobile() ? ' full' : ''}`}>
        <Routes>
          <Route index                  element={<AdminDashboard   admin={admin}/>}/>
          <Route path="magazines"       element={<AdminMagazines/>}/>
          <Route path="articles"        element={<AdminArticles/>}/>
          <Route path="uploads"         element={<AdminUploads/>}/>
          <Route path="subscribers"     element={<AdminSubscribers/>}/>
          <Route path="trends"          element={<AdminTrends/>}/>
        </Routes>
      </main>
    </div>
  );
}
